import { useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Users, Shield, Sparkles, Radio, AlertCircle, HelpCircle, Mail, UserPlus, Zap, Plus, Archive, Check, Heart } from "lucide-react";

// Cheapest XP upgrade is 5 (an attribute, or a skill at level 0)
const MIN_XP_SPEND = 5;

function Stat({ icon: Icon, label, value, sub, color = "amber", to, testid }) {
  return (
    <Link to={to} className="panel p-4 sm:p-5 block hover:border-[#FFB000] transition-all" data-testid={testid}>
      <div className="flex items-center gap-2 label-term mb-3"><Icon size={12} strokeWidth={1.5} /> {label}</div>
      <div className={`heading-mono text-3xl sm:text-4xl ${color}`}>{value}</div>
      <div className="mono text-[10px] text-[#A0A0A0] mt-1 min-h-[1rem] truncate">{sub}</div>
    </Link>
  );
}

function Avatar({ c }) {
  if (c.token_path) return <img src={fileUrl(c.token_path)} alt="" className="w-12 h-12 token-img object-cover rounded-full shrink-0" />;
  // First and last name, ignoring nicknames' quotes and other punctuation
  const letters = (c.name || "").split(/\s+/).map(w => (w.match(/[\p{L}\p{N}]/u) || [""])[0]).filter(Boolean);
  const initials = (letters.length > 1 ? letters[0] + letters[letters.length - 1] : letters[0] || "?").toUpperCase();
  return <div className="w-12 h-12 rounded-full border border-[rgba(255,176,0,0.4)] flex items-center justify-center heading-mono amber shrink-0">{initials}</div>;
}

function HealthBar({ health, max }) {
  const h = health || 0, m = max || 0;
  if (m <= 0) return null;
  const pct = Math.max(0, Math.min(100, (h / m) * 100));
  const tone = h <= 0 ? "#FF2A2A" : pct <= 50 ? "#FFB000" : "#4AF626";
  return (
    <div className="flex items-center gap-2 mono text-[10px]" title={`Health ${h}/${m}`}>
      <Heart size={10} strokeWidth={1.5} style={{ color: tone }} />
      <div className="flex-1 h-1.5 bg-[#222] min-w-[48px]"><div className="h-full" style={{ width: `${pct}%`, background: tone }} /></div>
      <span style={{ color: tone }}>{h <= 0 ? "BROKEN" : `${h}/${m}`}</span>
    </div>
  );
}

function ActionRow({ to, children, cta, tone = "amber", testid }) {
  return (
    <Link to={to} className="flex items-center gap-2 p-2 border-b border-[#222] hover:bg-[rgba(255,176,0,0.04)]" data-testid={testid}>
      <span className="mono text-xs flex-1 min-w-0 truncate">{children}</span>
      <span className={`mono text-[10px] ${tone} whitespace-nowrap`}>{cta}</span>
    </Link>
  );
}

function ActionGroup({ icon: Icon, label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-term mb-2 flex items-center gap-1"><Icon size={11} strokeWidth={1.5}/> {label}</div>
      {children}
    </div>
  );
}

function CampaignRow({ c, isGM, noCharacter }) {
  return (
    <Link to={`/campaigns/${c.id}`} className="block p-2 border-b border-[#222] hover:bg-[rgba(255,176,0,0.04)]" data-testid={`dash-camp-${c.id}`}>
      <div className="flex items-center gap-2">
        {isGM ? <Shield size={12} strokeWidth={1.5} className="amber shrink-0" /> : <Users size={12} strokeWidth={1.5} className="shrink-0" />}
        <span className={`mono truncate flex-1 ${isGM ? "amber" : ""}`}>{c.name}</span>
        {c.session_active
          ? <span className="mono text-[10px] neon-green whitespace-nowrap">● LIVE</span>
          : <span className="mono text-[10px] text-[#A0A0A0] whitespace-nowrap">○ IDLE</span>}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 pl-5 mono text-[10px] text-[#A0A0A0]">
        <span>{c.game_type}</span>
        <span>Party {(c.party_ids || []).length}</span>
        <span className="amber">${c.party_cash || 0}</span>
        {c.shop_open && <span className="neon-green">STORE OPEN</span>}
      </div>
      {noCharacter && (
        <div className="pl-5 mt-1 mono text-[10px] text-[#FF2A2A]" data-testid={`dash-camp-nochar-${c.id}`}>
          // none of your characters are assigned here — ask the GM to add one from the Party tab
        </div>
      )}
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [ai, setAi] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/dashboard").then(r => setD(r.data)).catch(() => setFailed(true));
    api.get("/dashboard/action-items").then(r => setAi(r.data)).catch(() => {});
  }, []);
  if (failed) return <div className="p-8 mono text-[#FF2A2A]">// Uplink failed — could not load the command deck. Refresh to retry.</div>;
  if (!d) return <div className="p-8 mono text-[#A0A0A0]">// Loading terminal...</div>;

  const chars = d.my_characters;
  const unassigned = chars.filter(c => !c.campaign_id).length;
  const readyToSpend = chars.filter(c => (c.experience_points || 0) >= MIN_XP_SPEND).length;
  const campCount = d.gm_campaigns.length + d.party_campaigns.length;
  const attendance = (ai?.live_sessions || []).filter(s => s.needs_attendance);
  const otherLive = (ai?.live_sessions || []).filter(s => !s.needs_attendance);

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
        <h1 className="heading-mono text-3xl sm:text-4xl amber amber-glow uppercase tracking-tight">// Command Deck</h1>
        <div className="mono text-xs text-[#A0A0A0] mt-1 tracking-widest uppercase">
          Welcome back, {user?.username}. {ai?.total ? `${ai.total} item${ai.total === 1 ? "" : "s"} need${ai.total === 1 ? "s" : ""} your attention.` : "All systems nominal."}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat icon={Users} label="Characters" value={chars.length} to="/characters" testid="stat-characters"
              sub={unassigned ? `${unassigned} not in a campaign` : chars.length ? "all assigned" : "create your first"} />
        <Stat icon={Shield} label="Campaigns" value={campCount} to="/campaigns" testid="stat-campaigns"
              sub={d.gm_campaigns.length ? `GM of ${d.gm_campaigns.length}` : campCount ? "as party member" : "none active"} />
        <Stat icon={Sparkles} label="Unspent XP" value={d.total_xp} color="neon-green" to="/characters" testid="stat-xp"
              sub={readyToSpend ? `${readyToSpend} character${readyToSpend === 1 ? "" : "s"} can upgrade` : "across all characters"} />
        <Stat icon={Radio} label="Live Sessions" value={d.active_sessions} color={d.active_sessions ? "neon-green" : "amber"} to="/campaigns" testid="stat-live"
              sub={d.active_sessions ? "in progress now" : "none running"} />
      </div>

      {ai && ai.total > 0 && (
        <div className="panel p-5 mb-6 border-green" data-testid="action-items-widget">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle size={16} strokeWidth={1.5} className="neon-green" />
            <div className="label-term neon-green">// My Action Items ({ai.total})</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            {attendance.length > 0 && (
              <ActionGroup icon={Check} label="Confirm Attendance" testid="ai-attendance">
                {attendance.map(s => (
                  <ActionRow key={s.id} to={`/campaigns/${s.id}`} cta="confirm →" tone="neon-green" testid={`ai-att-${s.id}`}>
                    <span className="neon-green mr-1">●</span>{s.name}
                    <span className="text-[#A0A0A0]"> · {s.unconfirmed_characters.join(", ")}</span>
                  </ActionRow>
                ))}
              </ActionGroup>
            )}
            {otherLive.length > 0 && (
              <ActionGroup icon={Zap} label="Live Sessions" testid="ai-live">
                {otherLive.map(s => (
                  <ActionRow key={s.id} to={`/campaigns/${s.id}`} cta={s.role === "gm" ? "run session →" : "join →"} testid={`ai-live-${s.id}`}>
                    <span className="neon-green mr-1">●</span>{s.name}
                    {s.role === "gm" && <span className="amber"> · GM</span>}
                  </ActionRow>
                ))}
              </ActionGroup>
            )}
            {ai.pending_questionnaires?.length > 0 && (
              <ActionGroup icon={HelpCircle} label="Pending Questionnaires" testid="ai-questionnaires">
                {ai.pending_questionnaires.map(q => (
                  <ActionRow key={q.session_id} to={`/campaigns/${q.campaign_id}`} cta="submit XP →" testid={`ai-quest-${q.session_id}`}>
                    <span className="amber mr-1">!</span>{q.campaign_name}
                  </ActionRow>
                ))}
              </ActionGroup>
            )}
            {ai.friend_requests?.length > 0 && (
              <ActionGroup icon={UserPlus} label="Friend Requests" testid="ai-friend-requests">
                {ai.friend_requests.map((fr, i) => (
                  <ActionRow key={i} to={`/characters/${fr.my_character_id}`} cta="review →" testid={`ai-fr-${i}`}>
                    <span className="amber">{fr.requester_character_name}</span> → <span className="amber">{fr.my_character_name}</span>
                  </ActionRow>
                ))}
              </ActionGroup>
            )}
            {ai.campaign_invites?.length > 0 && (
              <ActionGroup icon={Shield} label="Campaign Invites" testid="ai-invites">
                {ai.campaign_invites.map(n => (
                  <ActionRow key={n.id} to={n.link || "/campaigns"} cta="open →" testid={`ai-inv-${n.id}`}>{n.title}</ActionRow>
                ))}
              </ActionGroup>
            )}
            {ai.unread_dms?.length > 0 && (
              <ActionGroup icon={Mail} label={`New Messages${ai.unread_dms.length > 5 ? ` (${ai.unread_dms.length})` : ""}`} testid="ai-dms">
                {ai.unread_dms.slice(0, 5).map(n => (
                  <ActionRow key={n.id} to={n.link || "/friends"} cta="read →" testid={`ai-dm-${n.id}`}>{n.title}</ActionRow>
                ))}
              </ActionGroup>
            )}
          </div>
        </div>
      )}
      {ai && ai.total === 0 && (
        <div className="panel p-4 mb-6 mono text-xs text-[#A0A0A0]" data-testid="action-items-empty">
          // All clear — no pending action items.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="panel p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="label-term amber">// My Crew Roster</div>
            <Link to="/characters" className="mono text-[10px] amber hover:underline">all characters →</Link>
          </div>
          {chars.length === 0 && (
            <div className="space-y-3" data-testid="roster-empty">
              <div className="mono text-sm text-[#A0A0A0]">// No characters yet. Create one, then ask a GM to add it to their campaign.</div>
              <Link to="/characters" className="btn-amber inline-flex items-center gap-2 text-xs"><Plus size={12}/> Create a Character</Link>
            </div>
          )}
          <div className="space-y-3">
            {chars.map(c => {
              const ready = (c.experience_points || 0) >= MIN_XP_SPEND;
              return (
                <Link key={c.id} to={`/characters/${c.id}`} className="flex items-center gap-3 p-3 border border-[#222] hover:border-[#FFB000] transition-all" data-testid={`roster-${c.id}`}>
                  <Avatar c={c} />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="amber heading-mono uppercase truncate">{c.name}</span>
                      {ready && <span className="mono text-[9px] border border-[#4AF626] neon-green px-1 whitespace-nowrap" title="Enough XP for an upgrade">XP READY</span>}
                    </div>
                    <div className="mono text-[10px] text-[#A0A0A0] truncate">
                      {c.career || "—"} · {c.campaign_name ? <span className="text-[#E0E0E0]">{c.campaign_name}</span> : <span className="italic">not in a campaign</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1"><HealthBar health={c.health} max={c.max_health} /></div>
                      <span className={`mono text-[10px] whitespace-nowrap ${(c.stress_level || 0) > 0 ? "amber" : "text-[#A0A0A0]"}`} title="Stress level">
                        STRESS {c.stress_level || 0}
                      </span>
                    </div>
                  </div>
                  <div className="text-right mono text-xs shrink-0">
                    <div className="neon-green">{c.experience_points || 0} XP</div>
                    <div className="amber">${c.cash || 0}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="panel p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="label-term amber">// Campaigns</div>
            {d.ended_campaigns > 0 && (
              <Link to="/campaigns/archive" className="mono text-[10px] amber hover:underline flex items-center gap-1"><Archive size={10}/> archive ({d.ended_campaigns}) →</Link>
            )}
          </div>
          {campCount === 0 && (
            <div className="space-y-3" data-testid="campaigns-empty">
              <div className="mono text-sm text-[#A0A0A0]">// No active campaigns. Start your own as GM, or wait for a GM to add your character.</div>
              <Link to="/campaigns" className="btn-amber inline-flex items-center gap-2 text-xs"><Plus size={12}/> Start a Campaign</Link>
            </div>
          )}
          {d.gm_campaigns.length > 0 && (
            <div className="mb-4">
              <div className="label-term mb-1">As Game Master ({d.gm_campaigns.length})</div>
              {d.gm_campaigns.map(c => <CampaignRow key={c.id} c={c} isGM />)}
            </div>
          )}
          {d.party_campaigns.length > 0 && (
            <div>
              <div className="label-term mb-1">As Party ({d.party_campaigns.length})</div>
              {d.party_campaigns.map(c => <CampaignRow key={c.id} c={c} noCharacter={!chars.some(ch => ch.campaign_id === c.id)} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
